const SELECTORS = {
  chip: ".section-collection-quick-filters__chip",
  list: ".section-collection-quick-filters__list",
  scroll: ".section-collection-quick-filters__scroll",
  mainSection: ".shopify-section--main-collection",
};

const MASK_SIZE = 28;
const SCROLL_THRESHOLD = 2;
const SHOPIFY_SECTION_PREFIX = "shopify-section-";

const toSectionId = (elementId) => {
  if (!elementId) {
    return "";
  }

  return elementId.startsWith(SHOPIFY_SECTION_PREFIX)
    ? elementId.slice(SHOPIFY_SECTION_PREFIX.length)
    : elementId;
};

const getMainCollectionSectionId = () => {
  const mainSection = document.querySelector(SELECTORS.mainSection);

  return toSectionId(mainSection?.id);
};

const isDisabledChip = (chip) =>
  chip.classList.contains("is-disabled") || chip.getAttribute("aria-disabled") === "true";

const toggleFilterParam = (url, paramName, paramValue) => {
  const currentValues = url.searchParams.getAll(paramName);
  const nextValues = currentValues.includes(paramValue)
    ? currentValues.filter((value) => value !== paramValue)
    : [...currentValues, paramValue];

  url.searchParams.delete(paramName);

  for (const value of nextValues) {
    url.searchParams.append(paramName, value);
  }

  url.searchParams.delete("page");
  url.searchParams.delete("section_id");

  return url;
};

class CollectionQuickFilters extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.addEventListener("click", this.#onClick);
    document.addEventListener("facet:update", this.#onFacetUpdate);
    this.#scrollElement?.addEventListener("scroll", this.#onScroll, { passive: true });
    this.#updateScrollMask();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
    document.removeEventListener("facet:update", this.#onFacetUpdate);
    this.#scrollElement?.removeEventListener("scroll", this.#onScroll);
    this.#abortController?.abort();
  }

  get #scrollElement() {
    return this.querySelector(SELECTORS.scroll);
  }

  get #listElement() {
    return this.querySelector(SELECTORS.list);
  }

  get #sectionId() {
    return this.dataset.sectionId || toSectionId(this.closest(".shopify-section")?.id);
  }

  #onClick = (event) => {
    const chip = event.target.closest(SELECTORS.chip);

    if (!chip || !this.contains(chip)) {
      return;
    }

    if (isDisabledChip(chip)) {
      event.preventDefault();
      return;
    }

    const paramName = chip.dataset.paramName;
    const paramValue = chip.dataset.paramValue;
    const mainSectionId = getMainCollectionSectionId();

    if (!paramName || paramValue === undefined || !mainSectionId) {
      return;
    }

    event.preventDefault();

    const url = toggleFilterParam(new URL(window.location.href), paramName, paramValue);

    url.searchParams.set("section_id", mainSectionId);

    document.dispatchEvent(
      new CustomEvent("facet:update", {
        bubbles: true,
        detail: {
          url,
        },
      })
    );
  };

  #onFacetUpdate = (event) => {
    this.#refresh(event.detail?.url);
  };

  #onScroll = () => {
    this.#updateScrollMask();
  };

  #updateScrollMask() {
    const scroll = this.#scrollElement;

    if (!scroll) {
      return;
    }

    const maxScroll = scroll.scrollWidth - scroll.clientWidth;
    const canScroll = maxScroll > SCROLL_THRESHOLD;
    const fadeLeft = canScroll && scroll.scrollLeft > SCROLL_THRESHOLD;
    const fadeRight = canScroll && scroll.scrollLeft < maxScroll - SCROLL_THRESHOLD;

    scroll.style.setProperty("--quick-filters-mask-left", fadeLeft ? `${MASK_SIZE}px` : "0px");
    scroll.style.setProperty("--quick-filters-mask-right", fadeRight ? `${MASK_SIZE}px` : "0px");
  }

  #refresh = async (sourceUrl) => {
    const sectionId = this.#sectionId;

    if (!sectionId) {
      return;
    }

    const url = new URL(sourceUrl || window.location.href, window.location.origin);

    url.searchParams.delete("page");
    url.searchParams.set("section_id", sectionId);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
      });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const nextList = doc.querySelector(SELECTORS.list);
      const list = this.#listElement;

      if (list && nextList) {
        list.replaceWith(document.importNode(nextList, true));
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
    }

    this.#updateScrollMask();
  };
}

if (!customElements.get("collection-quick-filters")) {
  customElements.define("collection-quick-filters", CollectionQuickFilters);
}